// src/components/Timeline.js
import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import Faq from "../components/Faq";
import Footer from "../components/Footer";
import ChatButton from "../components/ChatButton";
import { Helmet } from "react-helmet-async";

const Timeline = () => {
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [changelog, setChangelog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    const fetchChangelog = async () => {
      try {
        const timestamp = new Date().getTime();
        const response = await fetch(`https://www.pixelliongroup.com/seotic/changelog.json?t=${timestamp}`);
        if (!response.ok) {
          throw new Error("Failed to fetch changelog");
        }
        const data = await response.json();
        setChangelog(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchChangelog();
  }, []);

  const toggleSection = (index) => {
    setExpandedIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  const getDotColor = (type) => {
    switch (type) {
      case "new-feature":
        return "bg-green-500";
      case "enhancement":
        return "bg-blue-500";
      case "bug-fix":
        return "bg-red-500";
      default:
        return "bg-gray-400";
    }
  };

  const versionToNumber = (v) => {
    return v
      .replace(/[^\d.]/g, "")
      .split(".")
      .map(Number);
  };

  const sortedChangelog = [...changelog].sort((a, b) => {
    const aVer = versionToNumber(a.version);
    const bVer = versionToNumber(b.version);
    for (let i = 0; i < Math.max(aVer.length, bVer.length); i++) {
      const aVal = aVer[i] || 0;
      const bVal = bVer[i] || 0;
      if (aVal !== bVal) return sortOrder === "desc" ? bVal - aVal : aVal - bVal;
    }
    return 0;
  });

  const filteredChangelog = sortedChangelog.filter((entry) => {
    const term = searchTerm.toLowerCase();
    return (
      entry.version.toLowerCase().includes(term) ||
      entry.description.toLowerCase().includes(term) ||
      entry.features.some((feature) => feature.text.toLowerCase().includes(term)) ||
      entry.fixes.some((fix) => fix.text.toLowerCase().includes(term))
    );
  });

  if (loading) return <p className="text-center mt-10">Loading changelog...</p>;
  if (error) return <p className="text-center text-red-500 mt-10">Error: {error}</p>;

  return (
    <>
      <Helmet>
        <title>Seotic Changelog - Powerful SEO Tools for Small Businesses</title>
        <meta name="description" content="Seotic provides easy to use SEO tools for small businesses to enhance online visibility, drive traffic, and grow your brand. Get started with Seotic today!" />
        <meta name="keywords" content="SEO, SEO tools, small business, digital marketing, online visibility, Seotic" />
        <link rel="icon" href="https://www.seotic.co/favicon.ico" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.seotic.co" />
        <meta property="og:title" content="Seotic - SEO Tools for Small Businesses" />
        <meta property="og:description" content="Optimize your website's SEO with Seotic's powerful, user friendly tools designed for small businesses. Start improving your search rankings today!" />
        <meta property="og:image" content="https://www.seotic.co/og-image.jpg" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://www.seotic.co" />
        <meta property="twitter:title" content="Seotic - SEO Tools for Small Businesses" />
        <meta property="twitter:description" content="Optimize your website's SEO with Seotic's powerful, user friendly tools designed for small businesses. Start improving your search rankings today!" />
        <meta property="twitter:image" content="https://www.seotic.co/og-image.jpg" />
      </Helmet>

      <Navbar />

      <section className="max-w-xl mx-auto p-6">
        <h2 className="text-4xl font-semibold text-center mb-4 mt-10">Changelog</h2>
        <p className="text-center text-gray-600 mb-6">
          Stay updated with our latest improvements, new features, and bug fixes. Use the search bar below to find specific updates.
        </p>

        <div className="flex md:justify-between mb-8 gap-3">
          <input
            type="text"
            placeholder="Search by version, feature, or fix..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none"
          />
          <button
            onClick={() => setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"))}
            className="w-fit text-sm px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100"
          >
             {sortOrder === "desc" ? <i class='bx bx-sort-up bx-sm' ></i> : <i class='bx bx-sort-down bx-sm' ></i>}
          </button>
        </div>

        <div className="relative border-l border-dashed border-gray-300">
          <div className="mb-20 ml-8 relative">
            <span className="absolute -left-9 top-2 flex items-center justify-center w-2 h-2 bg-white ring-2 ring-slate-500 rounded-full"></span>
            <h3 className="text-sm flex items-center font-normal text-slate-700 pt-1 uppercase">Up next</h3>
          </div>

          {filteredChangelog.length > 0 ? (
            filteredChangelog.map((entry, index) => (
              <div key={index} className="mb-20 ml-8 relative">
                <span className="absolute -left-9 top-3 flex items-center justify-center w-2 h-2 bg-white ring-2 ring-yellow-500 rounded-full"></span>

                <h3 className="mb-1 text-lg flex items-center font-medium text-gray-900">
                  Version {entry.version}{" "}
                  <span className="ml-2 text-sm font-normal text-gray-500">{entry.date}</span>
                </h3>
                <p className="text-gray-600 mb-3">{entry.description}</p>

                <button
                  onClick={() => toggleSection(index)}
                  className="text-blue-500 hover:text-blue-700 text-sm focus:outline-none"
                >
                  {expandedIndex === index ? "Show Less" : "Show More"}
                </button>

                {expandedIndex === index && (
                  <>
                    <div className="my-5">
                      <h4 className="font-medium text-lg ml-3 text-gray-800">Features</h4>
                      <ul className="list-none space-y-2 mt-1">
                        {entry.features.map((feature, i) => (
                          <li key={i} className="flex items-center">
                            <span className={`w-2 h-2 rounded-full -ml-9 mr-10 ${getDotColor(feature.type)}`}></span>
                            <span className="text-gray-700">{feature.text}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {entry.enhancement.length > 0 && (
                      <div className="mt-5">
                        <h4 className="font-medium text-lg ml-3 text-gray-800">Enhancement</h4>
                        <ul className="list-none space-y-2 mt-1">
                          {entry.enhancement.map((enh, i) => (
                            <li key={i} className="flex items-center">
                              <span className={`w-2 h-2 -ml-9 mr-10 rounded-full ${getDotColor(enh.type)}`}></span>
                              <span className="text-gray-700">{enh.text}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {entry.fixes.length > 0 && (
                      <div className="mt-5">
                        <h4 className="font-medium text-lg ml-3 text-gray-800">Fixes</h4>
                        <ul className="list-none space-y-2 mt-1">
                          {entry.fixes.map((fix, i) => (
                            <li key={i} className="flex items-center">
                              <span className={`w-2 h-2 -ml-9 mr-10 rounded-full ${getDotColor(fix.type)}`}></span>
                              <span className="text-gray-700">{fix.text}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))
          ) : (
            <p className="text-center text-gray-500 mt-6">No matching results found.</p>
          )}
        </div>
      </section>

      <ChatButton />
      <Faq />
      <Footer />
    </>
  );
};

export default Timeline;
